import type { Abi, Address } from "viem";
import type { Permission } from "@rhinestone/sdk";

export type DefinePermissionsConfig<TAbi extends Abi> = Permission<TAbi> & {
  /** Optional human-readable name surfaced in the review UI. */
  name?: string;
};

export interface DefinedPermissions<TAbi extends Abi = Abi> {
  /**
   * v2 SmartSession permissions, ready to pass to
   * `OneAuthClient.grantPermissions({ ...definePermissions(...) })`.
   *
   * Always a single-element array so the `...spread` pattern works cleanly
   * with `grantPermissions`, which accepts `permissions: Permission[]`.
   *
   * Widened to `Permission<Abi>` (rather than `Permission<TAbi>`) so the
   * spread composes with `GrantPermissionsOptions.permissions: Permission<Abi>[]`.
   * The SDK's v2 `Permission<TAbi>` includes conditional `SpendingLimitField<TFn>` /
   * `ValueLimitField<TFn>` types that break covariance over `TAbi`, so an inferred
   * narrow `Permission<erc20Abi>` is no longer assignable to `Permission<Abi>`.
   * We keep `TAbi` on the type parameter so `contracts[].abi` retains its
   * narrow shape for the review UI.
   */
  permissions: [Permission<Abi>];
  /**
   * Contract metadata for the permission review UI. The address and ABI
   * here mirror the entry in `permissions[0]`; `name` is optional and
   * purely cosmetic (it's never sent to a contract).
   */
  contracts: Array<{
    address: Address;
    name?: string;
    abi: TAbi;
  }>;
}

/**
 * Build a v2 SmartSession `Permission` from an ABI + function map.
 *
 * The Rhinestone SDK derives function selectors and universal-action
 * calldata offsets from the ABI internally, so app code no longer needs
 * to spell out raw 4-byte selectors or parameter indices. The accepted
 * shape is the SDK's own `Permission<TAbi>` plus an optional `name` for
 * the review UI.
 *
 * @example
 * const permissions = definePermissions({
 *   address: USDC,
 *   abi: erc20Abi,
 *   name: "USDC",
 *   functions: {
 *     transfer: {
 *       params: { to: { condition: "equal", value: RECIPIENT } },
 *     },
 *   },
 * });
 *
 * await oneAuth.grantPermissions({ ...permissions, sessionKeyAddress });
 */
export function definePermissions<const TAbi extends Abi>(
  config: DefinePermissionsConfig<TAbi>,
): DefinedPermissions<TAbi> {
  const { name, ...permission } = config;
  return {
    permissions: [permission as unknown as Permission<Abi>],
    contracts: [
      {
        address: config.address,
        name,
        abi: config.abi,
      },
    ],
  };
}
